import React, { useMemo, ReactNode, useCallback } from 'react';
import Fuse from 'fuse.js';
import { xor } from 'lodash-es';
import { useLocationPicker } from '@frontend/location-picker';
import { createContextWithSelectors as createContext, useContextSelector } from '@frontend/store';
import { useWorkspaceShallowStore } from '@frontend/workspace-scope';

interface SearchableEntity {
  id: string;
  name: string;
  type: 'organization' | 'group' | 'location';
  orgId: string;
  orgName: string;
  groupId?: string;
  groupName?: string;
  locationCount?: number;
  groupCount?: number;
}

export interface LocationGroupResult {
  groupId: string;
  groupName: string;
  orgId: string;
  orgName: string;
  locations: Array<{
    id: string;
    name: string;
  }>;
}

interface UnprivilegedPickerContextValue {
  orgIds: string[];
  currentOrgData: Record<string, string[]>;
  currentOrgLocationGroups: string[];
  searchableEntities: SearchableEntity[];
  searchResults: {
    locationGroups: LocationGroupResult[];
    groups: SearchableEntity[];
    organizations: SearchableEntity[];
  };
  orgLocationGroups: Record<
    string,
    Array<{ id: string; name: string; numberOfLocations: number; locations: string[] }>
  >;
  scopeHierarchy: Record<string, Record<string, string[]>>;
  hasSearchResults: boolean;
  selectedOrgId: string;
  selectedGroupId: string;
  selectedLocationIds: string[];
  searchResultCount: {
    locations: number;
    locationGroups: number;
    organizations: number;
  };
  isSimpleView?: boolean;
  hasInFlightEmptyGroup: boolean;
  hasAccessToGroupOnly: () => 'already-selected-empty-group' | 'available-empty-group' | undefined;
  closeModal?: () => void;
  getLocationNames: (locationIds: string[]) => string;
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  hasDifferentLocations: (selected: string[], inFlight: string[]) => boolean;
  hasSwitchedScope: boolean;
  renderOrgInfo?: (orgId: string) => JSX.Element | null;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
  renderBannerInfo?: () => JSX.Element | null;
  onApplyError?: (error: unknown) => void;
  onApply: (selectedOrgId: string, selectedGroupId: string, selectedLocationIds: string[]) => Promise<void>;
}

interface UnprivilegedPickerProviderProps {
  children: ReactNode;
  scopeHierarchy: Record<string, Record<string, string[]>>;
  selectedOrgId: string;
  selectedGroupId: string;
  selectedLocationIds: string[];
  selectedScope?: {
    scopeType?: string;
    scopeId?: string;
  };
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  closeModal?: () => void;
  renderOrgInfo?: (orgId: string) => JSX.Element | null;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
  renderBannerInfo?: () => JSX.Element | null;
  onApplyError?: (error: unknown) => void;
  onApply: (selectedOrgId: string, selectedGroupId: string, selectedLocationIds: string[]) => Promise<void>;
  isSimpleView?: boolean;
}

const UnprivilegedPickerContext = createContext<UnprivilegedPickerContextValue>(null!);

export const UnprivilegedPickerProvider: React.FC<UnprivilegedPickerProviderProps> = ({
  children,
  scopeHierarchy,
  getScopeName,
  selectedOrgId,
  selectedGroupId,
  selectedLocationIds,
  selectedScope,
  closeModal,
  renderOrgInfo,
  renderLocationInfo,
  renderBannerInfo,
  onApplyError,
  onApply,
  isSimpleView,
}) => {
  const {
    organizationViewId,
    debouncedSearchTerm,
    activeSearchFilter,
    inFlightOrganizationId,
    inFlightGroupId,
    inFlightLocationIds,
    isSearchMode,
  } = useLocationPicker([
    'organizationViewId',
    'debouncedSearchTerm',
    'activeSearchFilter',
    'inFlightOrganizationId',
    'inFlightGroupId',
    'inFlightLocationIds',
    'isSearchMode',
  ]);
  const { pendingWorkspaceId } = useWorkspaceShallowStore('pendingWorkspaceId');

  const orgIds = useMemo(() => Object.keys(scopeHierarchy), [scopeHierarchy]);

  const currentOrgData = useMemo(() => {
    return organizationViewId && scopeHierarchy[organizationViewId] ? scopeHierarchy[organizationViewId] : {};
  }, [scopeHierarchy, organizationViewId]);

  const currentOrgLocationGroups = useMemo(() => Object.keys(currentOrgData), [currentOrgData]);

  const searchableEntities = useMemo(() => {
    const entities: SearchableEntity[] = [];

    Object.entries(scopeHierarchy).forEach(([orgId, groups]) => {
      const orgName = getScopeName(orgId, 'org');
      const groupCount = Object.keys(groups).length;
      let totalLocationCount = 0;

      Object.entries(groups).forEach(([groupId, locations]) => {
        const groupName = getScopeName(groupId, 'group');
        totalLocationCount += locations.length;

        entities.push({
          id: groupId,
          name: groupName,
          type: 'group',
          orgId,
          orgName,
          locationCount: locations.length,
        });

        locations.forEach((locationId) => {
          entities.push({
            id: locationId,
            name: getScopeName(locationId, 'location'),
            type: 'location',
            orgId,
            orgName,
            groupId,
            groupName,
          });
        });
      });

      entities.push({
        id: orgId,
        name: orgName,
        type: 'organization',
        orgId,
        orgName,
        groupCount,
        locationCount: totalLocationCount,
      });
    });

    return entities;
  }, [scopeHierarchy, getScopeName]);

  const fuseInstance = useMemo(() => {
    const options = {
      keys: ['name'],
      threshold: 0.3,
      ignoreLocation: true,
    };
    return new Fuse(searchableEntities, options);
  }, [searchableEntities]);

  const searchResults = useMemo(() => {
    if (!isSearchMode || !debouncedSearchTerm) {
      return { locationGroups: [], groups: [], organizations: [] };
    }

    const results = fuseInstance.search(debouncedSearchTerm);

    const locationResults = results.filter((r) => r.item.type === 'location').map((r) => r.item);

    const locationGroups: Record<string, LocationGroupResult> = {};

    locationResults.forEach((location) => {
      const { groupId, groupName, orgId, orgName } = location;
      if (!groupId) return;

      if (!locationGroups[groupId]) {
        locationGroups[groupId] = {
          groupId,
          groupName: groupName || '',
          orgId,
          orgName,
          locations: [],
        };
      }

      locationGroups[groupId].locations.push({
        id: location.id,
        name: location.name,
      });
    });

    return {
      locationGroups: Object.values(locationGroups),
      groups: results.filter((r) => r.item.type === 'group').map((r) => r.item),
      organizations: results.filter((r) => r.item.type === 'organization').map((r) => r.item),
    };
  }, [debouncedSearchTerm, fuseInstance, isSearchMode]);

  const orgLocationGroups = useMemo(() => {
    const result: Record<
      string,
      Array<{ id: string; name: string; numberOfLocations: number; locations: string[] }>
    > = {};

    Object.entries(scopeHierarchy).forEach(([orgId, groups]) => {
      result[orgId] = Object.entries(groups).map(([groupId, locations]) => ({
        id: groupId,
        name: getScopeName(groupId, 'group'),
        numberOfLocations: locations.length,
        locations: locations,
      }));
    });

    return result;
  }, [scopeHierarchy, getScopeName]);

  const hasSearchResults = useMemo(() => {
    return (
      (!!searchResults.locationGroups.length && activeSearchFilter === 'locations') ||
      (!!searchResults.groups.length && activeSearchFilter === 'locationGroups') ||
      (!!searchResults.organizations.length && activeSearchFilter === 'organizations')
    );
  }, [searchResults, activeSearchFilter]);

  const searchResultCount = useMemo(
    () => ({
      locations: searchResults.locationGroups.reduce((acc, group) => acc + group.locations.length, 0) ?? 0,
      locationGroups: searchResults.groups.length ?? 0,
      organizations: searchResults.organizations.length ?? 0,
    }),
    [searchResults]
  );

  const getLocationNames = useCallback(
    (locationIds: string[]) =>
      locationIds.length > 1 ? `${locationIds.length} Locations` : getScopeName(locationIds?.[0], 'location'),
    [getScopeName]
  );

  const hasDifferentLocations = useCallback((selected: string[], inFlight: string[]) => {
    return xor(inFlight, selected).length > 0;
  }, []);

  const hasAccessToGroupOnly = useCallback(() => {
    if (isSearchMode || !organizationViewId) return;

    const currentOrgGroups = scopeHierarchy[organizationViewId] || {};
    const currentOrgGroupIds = Object.keys(currentOrgGroups);

    if (currentOrgGroupIds.length === 0) return;

    if (currentOrgGroupIds.length === 1 && currentOrgGroups[currentOrgGroupIds[0]].length === 0) {
      const singleGroupId = currentOrgGroupIds[0];

      return singleGroupId === selectedGroupId ? 'already-selected-empty-group' : 'available-empty-group';
    }

    return;
  }, [isSearchMode, organizationViewId, scopeHierarchy, selectedGroupId]);

  const hasInFlightEmptyGroup = useMemo(
    () =>
      scopeHierarchy[inFlightOrganizationId]?.[inFlightGroupId]?.length === 0 && inFlightGroupId !== selectedGroupId,
    [scopeHierarchy, inFlightOrganizationId, inFlightGroupId, selectedGroupId]
  );

  const hasSwitchedScope = useMemo(() => {
    const selectedWorkspaceScopeId = selectedScope?.scopeType === 'workspace' ? selectedScope.scopeId ?? null : null;
    const hasWorkspaceScopeChanged = pendingWorkspaceId !== selectedWorkspaceScopeId;

    return (
      inFlightOrganizationId !== (selectedOrgId ?? '') ||
      inFlightGroupId !== (selectedGroupId ?? '') ||
      hasDifferentLocations(selectedLocationIds, inFlightLocationIds) ||
      hasWorkspaceScopeChanged
    );
  }, [
    inFlightOrganizationId,
    inFlightGroupId,
    inFlightLocationIds,
    hasDifferentLocations,
    pendingWorkspaceId,
    selectedScope,
    selectedOrgId,
    selectedGroupId,
    selectedLocationIds,
  ]);

  const contextValue = useMemo(
    () => ({
      orgIds,
      currentOrgData,
      currentOrgLocationGroups,
      searchableEntities,
      searchResults,
      orgLocationGroups,
      hasSearchResults,
      searchResultCount,
      getLocationNames,
      hasDifferentLocations,
      hasSwitchedScope,
      getScopeName,
      scopeHierarchy,
      selectedOrgId,
      selectedGroupId,
      selectedLocationIds,
      closeModal,
      renderOrgInfo,
      renderLocationInfo,
      renderBannerInfo,
      onApplyError,
      onApply,
      isSimpleView,
      hasAccessToGroupOnly,
      hasInFlightEmptyGroup,
    }),
    [
      orgIds,
      currentOrgData,
      currentOrgLocationGroups,
      searchableEntities,
      searchResults,
      orgLocationGroups,
      hasSearchResults,
      searchResultCount,
      getLocationNames,
      hasDifferentLocations,
      hasSwitchedScope,
      getScopeName,
      scopeHierarchy,
      selectedOrgId,
      selectedGroupId,
      selectedLocationIds,
      closeModal,
      renderOrgInfo,
      renderLocationInfo,
      renderBannerInfo,
      onApplyError,
      onApply,
      isSimpleView,
      hasAccessToGroupOnly,
      hasInFlightEmptyGroup,
    ]
  );

  return <UnprivilegedPickerContext.Provider value={contextValue}>{children}</UnprivilegedPickerContext.Provider>;
};

export const useUnprivilegedPicker = <T extends any>(selector: (state: UnprivilegedPickerContextValue) => T) => {
  return useContextSelector(UnprivilegedPickerContext, selector);
};
